import type { I18n, MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import { Logger } from '@rover/rsdk/src/modules/Logging';

/**
 * Why an api call failed. `kind` records who wrote the text, which decides how it renders.
 * `ApiErrorMessage.fromDescriptor`, `ApiErrorMessage.fromApiResponse` and `diagnosticErrorString`
 * set it; `resolveApiErrorMessage` reads it.
 *
 * - `descriptor`: our copy, unresolved until a consumer resolves it with the request's `i18n`.
 * - `api`: prose the API sent, already in the user's language, with no catalog entry.
 * - `diagnostic`: internals such as `[object Object]`, never shown to a user.
 */
export type ApiErrorMessage =
  | { kind: 'descriptor'; descriptor: MessageDescriptor }
  | { kind: 'api'; text: string }
  | { kind: 'diagnostic'; text: string };

export type DescriptorApiErrorMessage = Extract<ApiErrorMessage, { kind: 'descriptor' }>;

/**
 * Copy we wrote, classified where it is declared so a call site passes the constant and
 * nothing else:
 * `export const GET_ERROR = ApiErrorMessage.fromDescriptor(msg({ id: 'apiError.pets.getError', … }))`.
 */
const fromDescriptor = (descriptor: MessageDescriptor): DescriptorApiErrorMessage => ({
  kind: 'descriptor',
  descriptor,
});

/**
 * The generic reason a call failed. Declared here, not in `parseApiErrors`, so resolving a
 * message does not pull the parser in.
 */
export const DEFAULT_ERROR_MESSAGE = fromDescriptor(
  msg({
    id: 'apiError.generic.default',
    message: `We were unable to process your request. Please try again shortly or contact Customer Support.`,
  })
);

/**
 * Developer-facing text: a stringified body, an axios `Network Error`, a duck's own literal.
 * Logged here rather than rendered. `parseApiErrors` and `fromApiResponse` classify into this;
 * a call site never needs to.
 */
export const diagnosticErrorString = (text: string): ApiErrorMessage => {
  // An API error body can echo what the user submitted, and Logger.warn forwards to Splunk,
  // so the log records the size of the text rather than the text.
  Logger.warn('API error message', { length: String(text.length) });

  // The browser console stays on the user's device, so a developer reproducing the failure
  // sees the text there. The SSR server's console is shipped like any other log.
  if (process.env.JS_ENV_CLIENT) {
    // eslint-disable-next-line no-console
    console.warn(`API error message: ${text}`);
  }

  return { kind: 'diagnostic', text };
};

/**
 * Whatever the API sent for the user, read out of a response: `"Enter a valid email address."`
 * is prose, already in the user's language, with no catalog entry to look up, so it renders as
 * sent. Anything that is not a string or a number would reach the user as `[object Object]`,
 * so it is diagnostic instead.
 */
const fromApiResponse = (value: unknown): ApiErrorMessage =>
  typeof value === 'string' || typeof value === 'number'
    ? { kind: 'api', text: String(value) }
    : diagnosticErrorString(JSON.stringify(value) ?? String(value));

/** The two ways a message comes to be: copy we wrote, or text the API sent. */
export const ApiErrorMessage = { fromDescriptor, fromApiResponse };

/**
 * Checks the tag and its payload, so another `{ kind }` union, such as a card attempt's outcome,
 * resolves to generic copy instead of reaching the UI as an object.
 */
export const isApiErrorMessage = (value: unknown): value is ApiErrorMessage => {
  if (typeof value !== 'object' || value === null) return false;
  const { kind, descriptor, text } = value as Record<string, unknown>;

  if (kind === 'descriptor') {
    return typeof descriptor === 'object' && descriptor !== null && 'id' in descriptor;
  }
  return (kind === 'api' || kind === 'diagnostic') && typeof text === 'string';
};

/**
 * An error holding why the call failed, still unresolved. Resolving at the throw would leave
 * the text outside React, where the `i18n` is.
 */
export interface WithApiErrorMessage {
  apiErrorMessage: ApiErrorMessage;
}

export const hasApiErrorMessage = (error: unknown): error is WithApiErrorMessage =>
  isApiErrorMessage((error as Partial<WithApiErrorMessage> | null)?.apiErrorMessage);

/**
 * The reason a caught value should report. A message passes through; an error with one gives
 * it up; anything else, an error with only its own `message` included, is diagnostic.
 */
export const toApiErrorMessage = (error: unknown): ApiErrorMessage => {
  if (isApiErrorMessage(error)) return error;
  if (hasApiErrorMessage(error)) return error.apiErrorMessage;

  const message = (error as { message?: unknown } | null)?.message;

  return diagnosticErrorString(typeof message === 'string' ? message : String(error));
};

/**
 * What to report for an api error in a log line or an analytics event. A descriptor reports
 * its `apiError.*` id, which survives translation, so one failure aggregates as one.
 */
export const toDiagnosticApiErrorMessage = (message: ApiErrorMessage): string =>
  message.kind === 'descriptor' ? String(message.descriptor.id) : message.text;

/** What resolving no message at all yields: the "nothing went wrong" sentinel. */
export const NO_API_ERROR_TEXT = '';

/**
 * Copy for a descriptor the catalog cannot resolve. A production build strips a descriptor's
 * text, so there is nothing to fall back to. Rendering this means the catalog is behind.
 */
export const UNRESOLVED_DESCRIPTOR_COPY =
  'We were unable to process your request. Please try again shortly or contact Customer Support.';

/**
 * Resolves against the request's `i18n`, never the `@lingui/core` global, which on the server
 * holds whichever request activated last.
 */
const resolveDescriptor = (descriptor: MessageDescriptor, i18n: I18n): string => {
  const resolved = i18n._(descriptor);

  // With no catalog entry and no `message` to fall back to, which is every descriptor in
  // production, Lingui returns the id, such as `apiError.generic.default`.
  if (resolved !== String(descriptor.id)) return resolved;

  // The generic default ships translated in every catalog, so a miss still renders in the
  // user's language. The literal is for a catalog that is missing that one too.
  return descriptor.id === DEFAULT_ERROR_MESSAGE.descriptor.id
    ? UNRESOLVED_DESCRIPTOR_COPY
    : resolveDescriptor(DEFAULT_ERROR_MESSAGE.descriptor, i18n);
};

/**
 * The user-facing text for a message, or for a caught error, which `toApiErrorMessage` reads
 * first. Api text goes through as sent; a `diagnostic` one renders generic copy in its place.
 * `null` and `undefined` are no error, and resolve to `NO_API_ERROR_TEXT`.
 */
export const resolveApiErrorMessage = (value: unknown, i18n: I18n): string => {
  if (value === null || value === undefined) return NO_API_ERROR_TEXT;

  const message = toApiErrorMessage(value);

  switch (message.kind) {
    case 'descriptor':
      return resolveDescriptor(message.descriptor, i18n);
    case 'api':
      return message.text;
    case 'diagnostic':
      return resolveDescriptor(DEFAULT_ERROR_MESSAGE.descriptor, i18n);
    default: {
      const exhaustiveCheck: never = message;
      return exhaustiveCheck;
    }
  }
};
